<template>
  <section class="mapping-page">
    <header class="page-header">
      <p class="eyebrow">Catalog Identity</p>
      <h2>ERP Material Mapping</h2>
      <p>Each Spyglass catalog family can link to multiple reviewed ERP assembly variants. Approving one link preserves the other variants.</p>
      <p class="muted">Confirmed identity → BOM/variant review → plant stock inspection. These are separate checks; a confirmed mapping does not imply available or orderable equipment. AI sizing does not yet rank by live inventory, pricing or lead time.</p>
    </header>

    <Card class="automation-panel">
      <template #title>Mapping Automation</template>
      <template #content>
        <div class="automation-grid">
          <label>
            Matching Mode
            <Dropdown
              v-model="automationMode"
              :options="automationModeOptions"
              option-label="label"
              option-value="value"
            />
          </label>
          <label>
            ERP Sources
            <Dropdown
              v-model="automationSource"
              :options="automationSourceOptions"
              option-label="label"
              option-value="value"
            />
          </label>
          <label>
            Batch Size
            <InputNumber
              v-model="automationLimit"
              :min="1"
              :max="includeBomEvidence ? 1 : 100"
              :disabled="includeBomEvidence"
              show-buttons
            />
          </label>
          <Button
            :label="
              automationMode === 'ai_assisted'
                ? 'Generate & AI Review'
                : 'Generate Candidates'
            "
            icon="pi pi-sparkles"
            :loading="generating"
            :disabled="
              automationMode === 'ai_assisted' && !aiReviewStatus.enabled
            "
            @click="generateCandidateBatch"
          />
        </div>
        <div class="flex align-items-center gap-2 mt-3">
          <InputSwitch v-model="discoverVariants" input-id="discover-variants" />
          <label for="discover-variants">Discover additional variants for already-linked families (review only)</label>
          <label>ERP result page <InputNumber v-model="erpSearchPage" :min="1" :max="1000" show-buttons aria-label="ERP result page" /></label>
        </div>
        <p class="muted">Search is bounded to 10 ERP search records per family/source/page, plus exact identities on page 1. Use later pages to inspect additional variants. Root collapsing and evidence filtering can reduce the candidates shown; an empty page does not establish full family coverage.</p>
        <p v-if="includeBomEvidence" role="status" class="muted">Effective batch: one catalog record; up to three unique BOM material reads. Every candidate stays proposed, including exact-number matches. BOM evidence does not change the score or auto-confirm a mapping.</p>

        <div
          v-if="automationMode === 'ai_assisted'"
          class="ai-review-status"
          role="status"
        >
          <Tag
            :value="
              aiReviewStatus.enabled ? 'AI review ready' : 'AI unavailable'
            "
            :severity="aiReviewStatus.enabled ? 'success' : 'danger'"
          />
          <span v-if="aiReviewStatus.enabled">
            {{ aiReviewStatus.modelId }} · {{ aiReviewStatus.region }}
          </span>
          <span v-else>
            {{ aiReviewStatus.disabledReason || "Bedrock is not configured." }}
          </span>
        </div>

        <div class="flex align-items-center gap-2 mt-3">
          <InputSwitch v-model="includeBomEvidence" input-id="bom-evidence" />
          <label for="bom-evidence">Include BOM evidence (review-only pilot: one catalog row, up to three material reads)</label>
          <InputText v-if="includeBomEvidence" v-model="bomPlant" placeholder="BOM plant (optional)" aria-label="BOM plant" maxlength="4" />
        </div>

        <div v-if="latestRun" class="run-strip" aria-live="polite">
          <div>
            <Tag
              :value="runStatusLabel(latestRun.status)"
              :severity="runStatusSeverity(latestRun.status)"
            />
            <span>{{ modeLabel(latestRun.mode) }}</span>
            <span v-if="latestRun.modelId">{{ latestRun.modelId }}</span>
          </div>
          <div>
            <strong>{{ latestRun.catalogCount }}</strong>
            <span>Catalog checked</span>
          </div>
          <div>
            <strong>{{ latestRun.candidateCount }}</strong>
            <span>Candidates</span>
          </div>
          <div>
            <strong>{{ latestRun.autoConfirmedCount }}</strong>
            <span>Exact auto-confirmed</span>
          </div>
          <div>
            <strong>{{ latestRun.unresolvedCount }}</strong>
            <span>No new candidates on this page</span>
          </div>
        </div>
        <p v-if="latestRun" class="muted">Latest {{ equipmentTypeLabel(equipmentType) }} run · Started {{ latestRun.startedAt }}<span v-if="latestRun.completedAt"> · Completed {{ latestRun.completedAt }}</span> · Algorithm {{ latestRun.algorithmVersion }}</p>
        <p v-if="latestRun?.errorMessage" role="alert">{{ latestRun.errorMessage }}</p>
        <p v-if="latestDiscovery" class="muted">{{ latestDiscovery.sourceSearches }} source searches · {{ latestDiscovery.confirmedLinksSkipped }} already-confirmed links skipped · {{ latestDiscovery.belowThresholdOrConflicting }} below threshold or conflicting. Full family coverage is not measured.</p>
        <div v-if="latestBomEvaluation" class="run-strip" aria-label="Latest run BOM coverage">
          <div><strong>{{ latestBomEvaluation.uniqueMaterialsRead ?? 'Unknown' }}</strong><span>Unique BOM materials inspected</span></div>
          <div><strong>{{ latestBomEvaluation.availableEvidence ?? 'Unknown' }}</strong><span>BOM evidence returned</span></div>
          <div><strong>{{ latestBomEvaluation.unavailableEvidence ?? 'Unknown' }}</strong><span>BOM reads unavailable</span></div>
          <p>Run-scoped lookup coverage, not mapping accuracy. Accuracy requires a reviewed reference set.</p>
        </div>
      </template>
    </Card>

    <Card>
      <template #content>
        <h3>BOM parent discovery</h3>
        <p>Shared BOMs link to either material master. Enter exact component numbers; select one ERP source above to resolve parent material identities.</p>
        <InputText v-model="bomComponents" placeholder="Component numbers, comma-separated" aria-label="BOM components" />
        <InputText v-model="bomPlant" placeholder="Plant code (required for stock)" aria-label="Inspection plant" maxlength="4" />
        <label v-if="equipmentType === 'pump'">Required assembly stages (optional) <InputNumber v-model="requiredStageCount" :min="1" :max="9999" aria-label="Required assembly stages" /></label>
        <p v-if="equipmentType === 'pump'" class="muted">When inspecting a confirmed link, compare its recorded stage count to this requirement. A stage match alone does not verify the complete assembly.</p>
        <Button label="Find parent materials" :loading="bomBusy" :disabled="automationSource === 'both'" @click="findBomParents" />
        <p v-if="bomBusy" role="status">{{ bomInspectionLabel }} — reading ERP evidence…</p>
        <p v-if="bomInspectionError" role="alert">{{ bomInspectionError }}</p>
        <ErpMappingEvidence v-if="bomInspection" :result="bomInspection" :title="bomInspectionLabel" />
      </template>
    </Card>

    <section class="candidate-section">
      <Card>
        <template #title>Assembly variants and remote-location stock</template>
        <template #content>
          <p>Enter explicit pump assembly attributes, then compare the confirmed variants of a catalog family. Plant quantities remain separate observations.</p>
          <InputText v-model="availabilityPlants" placeholder="Up to 3 plant codes, comma-separated" aria-label="Availability plants" />
          <InputText v-model="availabilityScenarioId" placeholder="Saved scenario UUID" aria-label="Availability scenario" />
          <Button label="Compare saved pump sections" :loading="availabilityBusy" :disabled="automationSource === 'both' || !availabilityScenarioId.trim()" @click="compareScenarioAvailability" />
          <textarea v-model="availabilityRequirements" rows="3" class="w-full" aria-label="Assembly requirements JSON" />
          <p class="muted">Supported: model, stageType, construction, arRatio, ratioDesignation, stageCount, housingNumber, shaftMaterial, coating, recirculation. Use exact known values; omit unknown requirements.</p>
          <p v-if="availabilityError" role="alert">{{ availabilityError }}</p>
          <ErpAssemblyAvailability v-if="availabilityResult" :result="availabilityResult" :title="availabilityTitle" />
          <template v-if="scenarioAvailabilityResult">
            <p>{{ scenarioAvailabilityResult.warning }}</p>
            <p>Source fingerprint: {{ scenarioAvailabilityResult.sourceFingerprint }} · {{ scenarioAvailabilityResult.stockReads }} stock reads.</p>
            <div v-for="section in scenarioAvailabilitySections" :key="String(section.sectionId)">
              <p>Section {{ section.position }} · {{ section.label }} · Installed requirements: {{ section.requirements }}</p>
              <p class="muted">Unchecked: {{ section.uncheckedRequirements }}</p>
              <p v-if="(section.missingCatalogAttributes as string[])?.length" class="muted">Missing catalog attributes: {{ section.missingCatalogAttributes }}</p>
              <p v-if="Object.keys(section.ambiguousAttributes as Record<string, unknown>).length" class="muted">Ambiguous catalog evidence: {{ section.ambiguousAttributes }}</p>
              <ErpAssemblyAvailability :result="section.comparison as Record<string, unknown>" :title="`Section ${section.position} variants`" />
            </div>
          </template>
        </template>
      </Card>
      <div class="section-heading">
        <div>
          <h3>Candidate Review</h3>
          <span>{{ candidateTotal }} matching candidates</span>
          <p class="muted">Review each family → ERP variant link independently. Scores are heuristic ranking values, not correctness probabilities. Approval does not establish exact design fit.</p>
        </div>
        <div class="candidate-filters">
          <Dropdown
            v-model="erpSourceFilter"
            :options="erpSourceFilterOptions"
            option-label="label"
            option-value="value"
            aria-label="ERP source"
          />
          <Dropdown
            v-model="candidateDecision"
            :options="candidateDecisionOptions"
            option-label="label"
            option-value="value"
            aria-label="Candidate decision status"
            @change="loadCandidateFirstPage"
          />
        </div>
      </div>

      <DataTable
        v-model:expanded-rows="candidateExpandedRows"
        class="candidate-table p-datatable-sm"
        data-key="id"
        :value="candidates"
        :loading="candidateLoading"
        :lazy="true"
        :paginator="true"
        :rows="candidatePageSize"
        :first="candidateOffset"
        :total-records="candidateTotal"
        :rows-per-page-options="[10, 25, 50]"
        responsive-layout="scroll"
        @page="onCandidatePage"
      >
        <template #empty>
          <div class="empty-state">No candidates in this review state.</div>
        </template>
        <Column expander class="expander-column" />
        <Column header="Spyglass Catalog">
          <template #body="{ data }">
            <div class="catalog-cell">
              <strong>{{ data.spyglassLabel }}</strong>
              <code>{{ data.spyglassPartNumber || "No part number" }}</code>
            </div>
          </template>
        </Column>
        <Column header="ERP Material">
          <template #body="{ data }">
            <div class="catalog-cell">
              <div class="material-identity">
                <code>{{ data.erpMaterialNumber }}</code>
                <Tag value="Root" severity="secondary" />
                <Tag
                  :value="erpSystemLabel(data.erpSystem)"
                  :severity="erpSystemSeverity(data.erpSystem)"
                />
              </div>
              <span>{{ data.erpDescription || "No description" }}</span>
            </div>
          </template>
        </Column>
        <Column header="Ranking score">
          <template #body="{ data }">
            <div class="score-cell">
              <strong>{{ confidenceLabel(data.finalScore) }}</strong>
              <span>
                Deterministic {{ confidenceLabel(data.deterministicScore) }}
              </span>
              <span v-if="data.aiScore != null">
                AI {{ confidenceLabel(data.aiScore) }}
              </span>
              <Tag
                v-if="aiReview(data)"
                :value="aiAssessmentLabel(data)"
                :severity="aiAssessmentSeverity(data)"
              />
            </div>
          </template>
        </Column>
        <Column header="Evidence">
          <template #body="{ data }">
            <div class="evidence-cell">
              <span>{{ sharedTokenLabel(data) }}</span>
              <span v-if="prefixedMaterialLabel(data)">
                {{ prefixedMaterialLabel(data) }}
              </span>
              <Tag
                v-if="data.conflicts.length"
                :value="`${data.conflicts.length} conflict(s)`"
                severity="warning"
              />
              <span v-else class="muted">No compared-value conflicts; unchecked attributes remain</span>
              <span>{{ variantReviewSummary(data) }}</span>
            </div>
          </template>
        </Column>
        <Column header="Decision">
          <template #body="{ data }">
            <Tag
              :value="decisionLabel(data.decisionStatus)"
              :severity="decisionSeverity(data.decisionStatus)"
            />
          </template>
        </Column>
        <Column header="Actions">
          <template #body="{ data }">
            <div v-if="data.decisionStatus === 'proposed'" class="row-actions">
              <Button
                v-tooltip="'Approve mapping'"
                icon="pi pi-check"
                severity="success"
                aria-label="Approve mapping"
                :loading="reviewingCandidateId === data.id"
                @click="reviewCandidate(data.id, 'approve')"
              />
              <Button
                v-tooltip="'Reject mapping'"
                icon="pi pi-times"
                severity="danger"
                outlined
                aria-label="Reject mapping"
                :disabled="reviewingCandidateId === data.id"
                @click="reviewCandidate(data.id, 'reject')"
              />
            </div>
            <span v-else class="muted">
              {{ data.decidedBy || "Automated" }}
            </span>
          </template>
        </Column>
        <template #expansion="{ data }">
          <div class="candidate-details">
            <ErpMappingEvidence v-if="data.evidence.bomEvidence" :result="data.evidence.bomEvidence" title="BOM evidence — review only" />
            <div v-if="aiReview(data)">
              <strong>AI Review</strong>
              <dl>
                <div>
                  <dt>Assessment</dt>
                  <dd>{{ aiAssessmentLabel(data) }}</dd>
                </div>
                <div>
                  <dt>Reason</dt>
                  <dd>{{ aiReview(data)?.reason }}</dd>
                </div>
                <div>
                  <dt>Model</dt>
                  <dd>{{ aiReview(data)?.modelId }}</dd>
                </div>
                <div v-if="aiReviewStrings(data, 'matchingAttributes').length">
                  <dt>Matching attributes</dt>
                  <dd>
                    {{ aiReviewStrings(data, "matchingAttributes").join(", ") }}
                  </dd>
                </div>
                <div v-if="aiReviewStrings(data, 'concerns').length">
                  <dt>Concerns</dt>
                  <dd>{{ aiReviewStrings(data, "concerns").join(", ") }}</dd>
                </div>
              </dl>
            </div>
            <div>
              <strong>Score Breakdown</strong>
              <dl>
                <div
                  v-for="[key, value] in Object.entries(data.scoreBreakdown)"
                  :key="key"
                >
                  <dt>{{ equipmentTypeLabel(key) }}</dt>
                  <dd>{{ confidenceLabel(Number(value)) }}</dd>
                </div>
              </dl>
            </div>
            <div>
              <strong>Conflicts</strong>
              <ul v-if="data.conflicts.length">
                <li v-for="(conflict, index) in data.conflicts" :key="index">
                  {{ equipmentTypeLabel(String(conflict.code || "conflict")) }}
                </li>
              </ul>
              <span v-else class="muted">No compared-value conflicts; unchecked attributes remain</span>
            </div>
            <div>
              <strong>Qualifiers</strong>
              <dl v-if="Object.keys(data.qualifiers).length">
                <div
                  v-for="[key, value] in Object.entries(data.qualifiers)"
                  :key="key"
                >
                  <dt>{{ equipmentTypeLabel(key) }}</dt>
                  <dd>{{ value }}</dd>
                </div>
              </dl>
              <span v-else class="muted">No variant qualifiers</span>
            </div>
            <div>
              <strong>Variant evidence — catalog / ERP</strong>
              <p class="muted">Description-derived attributes; assembly fit is not evaluated. ERP-only details distinguish variants under an umbrella family.</p>
              <dl v-if="parsedIdentityRows(data).length">
                <div
                  v-for="identity in parsedIdentityRows(data)"
                  :key="identity.field"
                >
                  <dt>{{ equipmentTypeLabel(identity.field) }}</dt>
                  <dd>
                    {{ identity.spyglass ?? "Unknown" }} /
                    {{ identity.erp ?? "Unknown" }} — {{ identity.status }}
                  </dd>
                </div>
              </dl>
              <span v-else class="muted">No structured variant data</span>
            </div>
          </div>
        </template>
      </DataTable>
    </section>

    <Card class="filter-panel">
      <template #content>
        <div class="filter-grid">
          <label>
            Equipment Type
            <Dropdown
              v-model="equipmentType"
              :options="equipmentOptions"
              option-label="label"
              option-value="value"
              @change="handleEquipmentChange"
            />
          </label>

          <label class="search-field">
            Catalog Search
            <span class="p-input-icon-left">
              <i class="pi pi-search" />
              <InputText
                v-model="searchText"
                type="search"
                placeholder="Part number, model, or manufacturer"
                @keydown.enter="loadFirstPage"
              />
            </span>
          </label>

          <label>
            ERP Source
            <Dropdown
              v-model="erpSourceFilter"
              :options="erpSourceFilterOptions"
              option-label="label"
              option-value="value"
            />
          </label>

          <label class="validation-toggle">
            <span>Verify mapped ERP records</span>
            <InputSwitch v-model="validateErpMaterials" input-id="verify-erp-materials" />
          </label>

          <label class="validation-toggle">
            <span>Summit only</span>
            <InputSwitch
              v-model="summitOnly"
              :disabled="!summitFilterAvailable"
            />
          </label>

          <div class="filter-actions">
            <Button
              label="Search Catalog"
              icon="pi pi-search"
              :loading="loading"
              @click="loadFirstPage"
            />
            <Button
              v-tooltip="'Clear search'"
              icon="pi pi-times"
              severity="secondary"
              outlined
              aria-label="Clear search"
              :disabled="!searchText"
              @click="clearSearch"
            />
          </div>
        </div>
      </template>
    </Card>

    <div v-if="error" class="error-banner" role="alert">{{ error }}</div>

    <div class="summary-strip" aria-live="polite">
      <div>
        <span class="summary-value">{{ totalCount }}</span>
        <span class="summary-label">Filtered catalog total</span>
      </div>
      <div>
        <span class="summary-value">{{ sourceCoverage.either }}</span>
        <span class="summary-label">Mapped on this page (either ERP)</span>
      </div>
      <div>
        <span class="summary-value">{{ sourceCoverage.ecc }}</span>
        <span class="summary-label">ECC mapped on page</span>
      </div>
      <div>
        <span class="summary-value">{{ sourceCoverage.s4 }}</span>
        <span class="summary-label">S4 mapped on page</span>
      </div>
      <div>
        <span class="summary-value">{{ pageCoverage.unresolved }}</span>
        <span class="summary-label">Unresolved on page</span>
      </div>
      <div>
        <Tag :value="validationLabel" :severity="validationSeverity" />
        <span class="summary-label">Material-master validation (not stock)</span>
      </div>
    </div>

    <DataTable
      v-model:expanded-rows="expandedRows"
      class="mapping-table p-datatable-sm"
      data-key="id"
      :value="rows"
      :loading="loading"
      :lazy="true"
      :paginator="true"
      :rows="pageSize"
      :first="offset"
      :total-records="totalCount"
      :rows-per-page-options="[10, 25, 50, 100]"
      current-page-report-template="Showing {first} to {last} of {totalRecords}"
      paginator-template="CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
      responsive-layout="scroll"
      striped-rows
      @page="onPage"
    >
      <template #empty>
        <div class="empty-state">No catalog records found.</div>
      </template>

      <Column expander class="expander-column" />
      <Column header="Spyglass Catalog">
        <template #body="{ data }">
          <div class="catalog-cell">
            <strong>{{ data.label }}</strong>
            <span>{{ data.manufacturer || "Unknown manufacturer" }}</span>
            <code>{{ data.spyglassMaterialId }}</code>
          </div>
        </template>
      </Column>
      <Column field="partNumber" header="Part Number">
        <template #body="{ data }">
          <code>{{ data.partNumber || "Not assigned" }}</code>
        </template>
      </Column>
      <Column header="Mapping Status">
        <template #body="{ data }">
          <Tag
            :value="mappingStatusLabel(data.erpMappingStatus)"
            :severity="mappingStatusSeverity(data.erpMappingStatus)"
          />
        </template>
      </Column>
      <Column header="ERP Materials">
        <template #body="{ data }">
          <div v-if="data.erpMaterials.length" class="material-number-list">
            <div
              v-for="mapping in data.erpMaterials"
              :key="
                mapping.mappingId ||
                `${mapping.erpSystem}:${mapping.materialNumber}`
              "
            >
              <code>{{ mapping.materialNumber }}</code>
              <Tag value="Root" severity="secondary" />
              <Tag
                :value="erpSystemLabel(mapping.erpSystem)"
                :severity="erpSystemSeverity(mapping.erpSystem)"
              />
            </div>
          </div>
          <span v-else class="muted">No confirmed mapping</span>
        </template>
      </Column>

      <template #expansion="{ data }">
        <div class="mapping-details">
          <div class="detail-heading">
            <div>
              <strong>{{ data.label }}</strong>
              <span
                >{{ data.erpMaterials.length }} confirmed ERP material(s)</span
              >
            </div>
          </div>

          <div v-if="data.erpMaterials.length" class="erp-material-grid">
            <article
              v-for="mapping in data.erpMaterials"
              :key="
                mapping.mappingId ||
                `${mapping.erpSystem}:${mapping.materialNumber}`
              "
              class="erp-material"
            >
              <div class="material-heading">
                <div class="material-identity">
                  <code>{{ mapping.materialNumber }}</code>
                  <Tag value="Root" severity="secondary" />
                  <Tag
                    :value="erpSystemLabel(mapping.erpSystem)"
                    :severity="erpSystemSeverity(mapping.erpSystem)"
                  />
                </div>
                <Tag :value="mapping.mappingKind || 'exact'" severity="info" />
              </div>
              <strong>
                {{
                  mapping.erpMaterial?.description ||
                  mapping.spyglassPartNumber ||
                  "Material master not loaded"
                }}
              </strong>
              <Button label="Inspect BOM / plant stock" :loading="bomBusy" @click="inspectMapping(data.spyglassMaterialId, mapping)" />
              <Button v-if="equipmentType === 'pump'" label="Compare family variants / locations" :loading="availabilityBusy" @click="compareAvailability(data.spyglassMaterialId, mapping.erpSystem)" />
              <dl>
                <div>
                  <dt>Confidence</dt>
                  <dd>{{ confidenceLabel(mapping.confidence) }}</dd>
                </div>
                <div>
                  <dt>Mapping Source</dt>
                  <dd>{{ sourceLabel(mapping.source) }}</dd>
                </div>
                <div>
                  <dt>ERP Status</dt>
                  <dd>
                    <Tag
                      :value="verificationLabel(mapping.erpVerificationStatus)"
                      :severity="
                        verificationSeverity(mapping.erpVerificationStatus)
                      "
                    />
                  </dd>
                </div>
                <div>
                  <dt>Product Hierarchy</dt>
                  <dd>
                    {{ mapping.erpMaterial?.prod_hierarchy || "Not loaded" }}
                  </dd>
                </div>
                <div>
                  <dt>Base UOM</dt>
                  <dd>{{ baseUomLabel(mapping.erpMaterial?.base_uom) }}</dd>
                </div>
                <div>
                  <dt>Verified By</dt>
                  <dd>{{ mapping.verifiedBy || "Not recorded" }}</dd>
                </div>
              </dl>
              <div
                v-if="Object.keys(mapping.qualifiers || {}).length"
                class="qualifiers"
              >
                <span
                  v-for="[key, value] in Object.entries(mapping.qualifiers)"
                  :key="key"
                >
                  <strong>{{ key }}:</strong> {{ value }}
                </span>
              </div>
            </article>
          </div>

          <div v-else class="unresolved-detail">
            No confirmed ERP material is linked to this Spyglass catalog record.
          </div>
        </div>
      </template>
    </DataTable>
  </section>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from "vue";
import type { DataTablePageEvent } from "primevue/datatable";
import { useToast } from "primevue/usetoast";

import {
  decideErpMappingCandidate,
  generateErpMappingCandidates,
  inspectErpMapping,
  compareErpAssemblyAvailability,
  compareScenarioAssemblyAvailability,
  discoverErpBomParents,
  getErpMappingAiReviewStatus,
  listErpMappingCandidates,
  listErpMappingRuns,
  resolveErpMaterialMappings,
  searchMaterials,
  type ErpMappingAutomationMode,
  type ErpMappingAiReviewStatus,
  type ErpMappingCandidate,
  type ErpMappingCandidateDecision,
  type ErpMappingRun,
  type ErpMappingStatus,
  type ErpMaterialMapping,
  type ErpMaterialMappingResolution,
  type ErpSystem
} from "@/libs/aiDos/adminApi";
import {
  catalogSearchFilters,
  filterCatalogMappingsByErpSystem,
  mappingPageCoverage,
  mappingSourceCoverage,
  mergeCatalogMappingResolution,
  type CatalogMappingRow
} from "@/libs/aiDos/adminErpMappings";
import ErpMappingEvidence from "@/components/ai/ErpMappingEvidence.vue";
import ErpAssemblyAvailability from "@/components/ai/ErpAssemblyAvailability.vue";
import {
  equipmentTypeLabel,
  errorMessage,
  MATERIAL_SEARCH_EQUIPMENT_TYPES
} from "@/libs/aiDos/adminShared";

const equipmentOptions = MATERIAL_SEARCH_EQUIPMENT_TYPES.map(value => ({
  label: equipmentTypeLabel(value),
  value
}));

const toast = useToast();
const equipmentType = ref("pump");
const searchText = ref("");
const validateErpMaterials = ref(false);
const summitOnly = ref(true);
const rows = ref<CatalogMappingRow[]>([]);
const expandedRows = ref<Record<string, boolean>>({});
const loading = ref(false);
const error = ref<string | null>(null);
const totalCount = ref(0);
const pageSize = ref(25);
const offset = ref(0);
const automationMode = ref<ErpMappingAutomationMode>("deterministic");
const includeBomEvidence = ref(false);
const discoverVariants = ref(true);
const erpSearchPage = ref(1);
const bomPlant = ref("");
const bomComponents = ref("");
const requiredStageCount = ref<number | null>(null);
const automationSource = ref<ErpSystem | "both">("both");
const availabilityPlants = ref('');
const availabilityScenarioId = ref('');
const scenarioAvailabilityResult = ref<Record<string, unknown> | null>(null);
const scenarioAvailabilitySections = computed(() => Array.isArray(scenarioAvailabilityResult.value?.sections)
  ? scenarioAvailabilityResult.value.sections as Array<Record<string, unknown>> : []);
const availabilityRequirements = ref('{}');
const availabilityBusy = ref(false);
const availabilityResult = ref<Record<string, unknown> | null>(null);
const availabilityError = ref<string | null>(null);
const availabilityTitle = ref('Assembly comparison');
let availabilityRequest = 0;
const compareAvailability = async (catalogId: string, source: ErpSystem) => {
  const request = ++availabilityRequest;
  availabilityBusy.value = true;
  availabilityResult.value = null;
  scenarioAvailabilityResult.value = null;
  availabilityError.value = null;
  availabilityTitle.value = `${source.toUpperCase()} family ${catalogId}`;
  try {
    const requirements = JSON.parse(availabilityRequirements.value);
    const result = await compareErpAssemblyAvailability({ equipmentType: equipmentType.value, catalogId, erpSystem: source,
      requirements, plants: availabilityPlants.value.split(',').map(value => value.trim()).filter(Boolean) });
    if (request === availabilityRequest) availabilityResult.value = result;
  } catch (error) { if (request === availabilityRequest) availabilityError.value = errorMessage(error); }
  finally { if (request === availabilityRequest) availabilityBusy.value = false; }
};
const compareScenarioAvailability = async () => {
  if (automationSource.value === 'both') return;
  const request = ++availabilityRequest;
  availabilityBusy.value = true;
  availabilityResult.value = null;
  scenarioAvailabilityResult.value = null;
  availabilityError.value = null;
  try {
    const result = await compareScenarioAssemblyAvailability({ scenarioId: availabilityScenarioId.value.trim(),
      erpSystem: automationSource.value, plants: availabilityPlants.value.split(',').map(value => value.trim()).filter(Boolean) });
    if (request === availabilityRequest) scenarioAvailabilityResult.value = result;
  } catch (error) { if (request === availabilityRequest) availabilityError.value = errorMessage(error); }
  finally { if (request === availabilityRequest) availabilityBusy.value = false; }
};
watch([equipmentType, automationSource, availabilityPlants, availabilityRequirements, availabilityScenarioId], () => {
  availabilityRequest++;
  availabilityResult.value = null;
  scenarioAvailabilityResult.value = null;
  availabilityError.value = null;
  availabilityBusy.value = false;
});
const bomBusy = ref(false);
const bomInspection = ref<Record<string, unknown> | null>(null);
const bomInspectionLabel = ref("Inspection result");
const bomInspectionError = ref<string | null>(null);
const bomFingerprints = new Map<string, string>();
const findBomParents = async () => {
  if (automationSource.value === "both") return;
  bomBusy.value = true;
  bomInspection.value = null;
  bomInspectionError.value = null;
  bomInspectionLabel.value = `Parent discovery · ${automationSource.value.toUpperCase()} · ${bomPlant.value.trim() || 'All plants'}`;
  try {
    bomInspection.value = await discoverErpBomParents({ erpSystem: automationSource.value,
      componentNumbers: bomComponents.value.split(",").map(v => v.trim()).filter(Boolean),
      plant: bomPlant.value.trim() || undefined });
  } catch (e) { bomInspectionError.value = errorMessage(e); }
  finally { bomBusy.value = false; }
};
const inspectMapping = async (catalogId: string, mapping: { erpSystem: ErpSystem; materialNumber: string }) => {
  const key = `${catalogId}:${mapping.erpSystem}:${mapping.materialNumber}:${bomPlant.value.trim()}`;
  bomBusy.value = true;
  bomInspection.value = null;
  bomInspectionError.value = null;
  bomInspectionLabel.value = `Confirmed mapping · ${mapping.erpSystem.toUpperCase()} ${mapping.materialNumber} · ${bomPlant.value.trim() || 'No stock plant selected'}`;
  try {
    const result = await inspectErpMapping({ equipmentType: equipmentType.value, catalogId,
      ...mapping, plant: bomPlant.value.trim() || undefined,
      previousFingerprint: bomFingerprints.get(key), includeInventory: Boolean(bomPlant.value.trim()),
      assemblyRequirements: equipmentType.value === "pump" && requiredStageCount.value != null ? { stageCount: requiredStageCount.value } : undefined });
    bomInspection.value = result;
    const evidence = result.bomEvidence as { bomFingerprint?: string } | undefined;
    if (evidence?.bomFingerprint && !bomFingerprints.has(key)) bomFingerprints.set(key, evidence.bomFingerprint);
  } catch (e) { bomInspectionError.value = errorMessage(e); }
  finally { bomBusy.value = false; }
};
const erpSourceFilter = ref<ErpSystem | "">("");
const automationLimit = ref(25);
const generating = ref(false);
const aiReviewStatus = ref<ErpMappingAiReviewStatus>({
  enabled: false,
  modelId: null,
  region: "",
  reviewVersion: "",
  disabledReason: "Checking Bedrock configuration."
});
const runs = ref<ErpMappingRun[]>([]);
const candidates = ref<ErpMappingCandidate[]>([]);
const candidateExpandedRows = ref<Record<string, boolean>>({});
const candidateDecision = ref<ErpMappingCandidateDecision | "">("proposed");
const candidateLoading = ref(false);
const candidateTotal = ref(0);
const candidatePageSize = ref(10);
const candidateOffset = ref(0);
const reviewingCandidateId = ref<string | null>(null);
const validation = ref<ErpMaterialMappingResolution["erpValidation"]>({
  requested: false,
  status: "not_requested",
  checkedAt: null,
  warning: null,
  sources: {}
});

const automationModeOptions = [
  { label: "Deterministic", value: "deterministic" },
  { label: "AI-assisted", value: "ai_assisted" }
];
const automationSourceOptions = [
  { label: "ECC and S4", value: "both" },
  { label: "ECC", value: "ecc" },
  { label: "S4", value: "s4" }
];
const erpSourceFilterOptions = [
  { label: "All ERP sources", value: "" },
  { label: "ECC", value: "ecc" },
  { label: "S4", value: "s4" }
];
const candidateDecisionOptions = [
  { label: "Proposed", value: "proposed" },
  { label: "Auto-confirmed", value: "auto_confirmed" },
  { label: "Approved", value: "approved" },
  { label: "Rejected", value: "rejected" },
  { label: "All decisions", value: "" }
];

const pageCoverage = computed(() => mappingPageCoverage(rows.value));
const sourceCoverage = computed(() => mappingSourceCoverage(rows.value));
const latestRun = computed(() => runs.value[0] || null);
const latestBomEvaluation = computed(() => {
  const value = latestRun.value?.requestConfig?.bomEvaluation;
  return value && typeof value === "object" ? value as Record<string, unknown> : null;
});
const summitFilterAvailable = computed(
  () => equipmentType.value !== "pump_housing"
);
const summitFilterActive = computed(
  () => summitOnly.value && summitFilterAvailable.value
);

const validationLabel = computed(() => {
  if (!validateErpMaterials.value) return "Not requested";
  if (validation.value.status === "complete") return "Complete";
  if (validation.value.status === "partial") return "Partial";
  if (validation.value.status === "not_applicable") return "No mappings";
  if (validation.value.status === "unavailable") return "Unavailable";
  return "Pending";
});

const validationSeverity = computed(() => {
  if (validation.value.status === "complete") return "success";
  if (validation.value.status === "partial") return "warning";
  if (validation.value.status === "unavailable") return "danger";
  return "info";
});

const loadCatalog = async () => {
  loading.value = true;
  error.value = null;
  expandedRows.value = {};
  try {
    const result = await searchMaterials({
      equipmentType: equipmentType.value,
      search: searchText.value.trim() || undefined,
      filters: catalogSearchFilters(
        summitOnly.value,
        summitFilterAvailable.value
      ),
      limit: pageSize.value,
      offset: offset.value
    });
    totalCount.value = result.totalCount;
    rows.value = filterCatalogMappingsByErpSystem(
      result.items,
      erpSourceFilter.value || undefined
    );

    if (validateErpMaterials.value && result.items.length) {
      const resolution = await resolveErpMaterialMappings(
        equipmentType.value,
        result.items.map(item => item.spyglassMaterialId),
        true
      );
      validation.value = resolution.erpValidation;
      rows.value = filterCatalogMappingsByErpSystem(
        mergeCatalogMappingResolution(result.items, resolution),
        erpSourceFilter.value || undefined
      );
      if (resolution.erpValidation.warning) {
        error.value = resolution.erpValidation.warning;
      }
    } else if (validateErpMaterials.value) {
      validation.value = {
        requested: true,
        status: "not_applicable",
        checkedAt: null,
        warning: null,
        sources: {}
      };
    } else {
      validation.value = {
        requested: false,
        status: "not_requested",
        checkedAt: null,
        warning: null,
        sources: {}
      };
    }
  } catch (err) {
    rows.value = [];
    totalCount.value = 0;
    error.value = errorMessage(err);
  } finally {
    loading.value = false;
  }
};

const loadRuns = async () => {
  const result = await listErpMappingRuns(equipmentType.value);
  runs.value = result.runs;
};

const loadAiReviewStatus = async () => {
  try {
    aiReviewStatus.value = await getErpMappingAiReviewStatus();
  } catch (err) {
    aiReviewStatus.value = {
      enabled: false,
      modelId: null,
      region: "",
      reviewVersion: "",
      disabledReason: errorMessage(err)
    };
  }
};

const loadCandidates = async () => {
  candidateLoading.value = true;
  candidateExpandedRows.value = {};
  try {
    const result = await listErpMappingCandidates({
      equipmentType: equipmentType.value,
      decisionStatus: candidateDecision.value,
      erpSystem: erpSourceFilter.value || undefined,
      limit: candidatePageSize.value,
      offset: candidateOffset.value
    });
    candidates.value = result.candidates;
    candidateTotal.value = result.totalCount;
  } catch (err) {
    candidates.value = [];
    candidateTotal.value = 0;
    error.value = errorMessage(err);
  } finally {
    candidateLoading.value = false;
  }
};

const loadCandidateFirstPage = () => {
  candidateOffset.value = 0;
  return loadCandidates();
};

const generateCandidateBatch = async () => {
  generating.value = true;
  error.value = null;
  try {
    const result = await generateErpMappingCandidates({
      equipmentType: equipmentType.value,
      mode: automationMode.value,
      search: searchText.value.trim() || undefined,
      summitOnly: summitFilterActive.value,
      includeBomEvidence: includeBomEvidence.value,
      discoverVariants: discoverVariants.value,
      erpSearchPage: erpSearchPage.value || 1,
      bomComponentNumbers: includeBomEvidence.value ? bomComponents.value.split(",").map(v => v.trim()).filter(Boolean) : undefined,
      bomPlant: includeBomEvidence.value ? bomPlant.value.trim() || undefined : undefined,
      erpSystems:
        automationSource.value === "both"
          ? ["ecc", "s4"]
          : [automationSource.value],
      limit: automationLimit.value || 25,
      offset: offset.value
    });
    toast.add({
      severity: "success",
      summary: "Mapping run completed",
      detail: `${result.run.candidateCount} candidates generated`,
      life: 5000
    });
    await Promise.all([loadRuns(), loadCandidateFirstPage(), loadCatalog()]);
  } catch (err) {
    error.value = errorMessage(err);
  } finally {
    generating.value = false;
  }
};

const reviewCandidate = async (
  candidateId: string,
  decision: "approve" | "reject"
) => {
  reviewingCandidateId.value = candidateId;
  error.value = null;
  try {
    await decideErpMappingCandidate(candidateId, decision);
    toast.add({
      severity: decision === "approve" ? "success" : "info",
      summary:
        decision === "approve" ? "Mapping approved" : "Candidate rejected",
      life: 4000
    });
    await Promise.all([loadCandidates(), loadCatalog()]);
  } catch (err) {
    error.value = errorMessage(err);
  } finally {
    reviewingCandidateId.value = null;
  }
};

const loadFirstPage = () => {
  offset.value = 0;
  return loadCatalog();
};

const handleEquipmentChange = () => {
  offset.value = 0;
  candidateOffset.value = 0;
  Promise.all([loadCatalog(), loadRuns(), loadCandidates()]);
};

const clearSearch = () => {
  searchText.value = "";
  loadFirstPage();
};

const onPage = (event: DataTablePageEvent) => {
  pageSize.value = event.rows;
  offset.value = event.first;
  loadCatalog();
};

const onCandidatePage = (event: DataTablePageEvent) => {
  candidatePageSize.value = event.rows;
  candidateOffset.value = event.first;
  loadCandidates();
};

const mappingStatusLabel = (status: ErpMappingStatus) => {
  if (status === "confirmed") return "Mapped";
  if (status === "unresolved") return "Unmapped";
  return "Unavailable";
};

const mappingStatusSeverity = (status: ErpMappingStatus) => {
  if (status === "confirmed") return "success";
  if (status === "unresolved") return "warning";
  return "danger";
};

const confidenceLabel = (confidence?: number | null) =>
  confidence == null ? "Not recorded" : `${Math.round(confidence * 100)}%`;

const sourceLabel = (source?: string | null) =>
  source ? equipmentTypeLabel(source) : "Not recorded";

const erpSystemLabel = (erpSystem: ErpSystem) =>
  erpSystem === "s4" ? "S4" : "ECC";

const erpSystemSeverity = (erpSystem: ErpSystem) =>
  erpSystem === "s4" ? "info" : "secondary";

const verificationLabel = (
  status: ErpMaterialMapping["erpVerificationStatus"]
) => {
  if (status === "not_requested") return "Not requested";
  if (status === "not_found") return "Not found";
  if (status === "source_unavailable") return "Source unavailable";
  return equipmentTypeLabel(status);
};

const verificationSeverity = (
  status: ErpMaterialMapping["erpVerificationStatus"]
) => {
  if (status === "verified") return "success";
  if (
    status === "not_found" ||
    status === "deleted" ||
    status === "source_unavailable"
  )
    return "danger";
  if (status === "inactive") return "warning";
  return "info";
};

const baseUomLabel = (value?: string | Record<string, unknown> | null) => {
  if (!value) return "Not loaded";
  if (typeof value === "string") return value;
  return String(value.code || value.name || "Available");
};

const modeLabel = (mode: ErpMappingAutomationMode) =>
  mode === "ai_assisted" ? "AI-assisted" : "Deterministic";

const runStatusLabel = (status: ErpMappingRun["status"]) =>
  status === "completed"
    ? "Completed"
    : status === "failed"
      ? "Failed"
      : "Running";

const runStatusSeverity = (status: ErpMappingRun["status"]) => {
  if (status === "completed") return "success";
  if (status === "failed") return "danger";
  return "info";
};

const decisionLabel = (status: ErpMappingCandidateDecision) =>
  equipmentTypeLabel(status);

const decisionSeverity = (status: ErpMappingCandidateDecision) => {
  if (status === "approved" || status === "auto_confirmed") return "success";
  if (status === "rejected") return "danger";
  return "warning";
};

const sharedTokenLabel = (candidate: ErpMappingCandidate) => {
  const sharedTokens = candidate.evidence.sharedTokens;
  if (!Array.isArray(sharedTokens) || !sharedTokens.length) {
    return "No shared identity tokens";
  }
  return sharedTokens.slice(0, 6).join(", ");
};

const prefixedMaterialLabel = (candidate: ErpMappingCandidate) => {
  const prefixed = candidate.evidence.prefixedMaterialNumbers;
  if (!Array.isArray(prefixed) || !prefixed.length) return "";
  return `${prefixed.length} prefixed variant(s) collapsed`;
};

const aiReview = (candidate: ErpMappingCandidate) => {
  const review = candidate.evidence.aiReview;
  if (!review || typeof review !== "object" || Array.isArray(review)) {
    return null;
  }
  return review as Record<string, unknown>;
};

const aiAssessmentLabel = (candidate: ErpMappingCandidate) => {
  const assessment = String(aiReview(candidate)?.assessment || "");
  return assessment ? equipmentTypeLabel(assessment) : "AI reviewed";
};

const aiAssessmentSeverity = (candidate: ErpMappingCandidate) => {
  const assessment = aiReview(candidate)?.assessment;
  if (assessment === "exact_match" || assessment === "likely_match") {
    return "success";
  }
  if (assessment === "not_match") return "danger";
  return "warning";
};

const aiReviewStrings = (
  candidate: ErpMappingCandidate,
  field: "matchingAttributes" | "concerns"
) => {
  const value = aiReview(candidate)?.[field];
  return Array.isArray(value) ? value.map(item => String(item)) : [];
};

const parsedIdentityRows = (candidate: ErpMappingCandidate) => {
  const assessment = candidate.evidence.variantAssessment as { attributes?: Array<{ field: string; catalog: unknown; erp: unknown; status: string }> } | undefined;
  if (Array.isArray(assessment?.attributes)) {
    return assessment.attributes.map(attribute => ({
      field: attribute.field, spyglass: attribute.catalog, erp: attribute.erp,
      status: attribute.status.replaceAll("_", " ")
    }));
  }
  const variant = candidate.evidence.variantIdentity;
  if (!variant || typeof variant !== "object" || Array.isArray(variant)) {
    return [];
  }
  const sides = variant as {
    spyglass?: Record<string, unknown>;
    erp?: Record<string, unknown>;
  };
  const fields = new Set([
    ...Object.keys(sides.spyglass || {}),
    ...Object.keys(sides.erp || {})
  ]);
  return Array.from(fields).map(field => ({
    field,
    spyglass: sides.spyglass?.[field],
    erp: sides.erp?.[field],
    status: "Legacy evidence — re-discover for attribute assessment"
  }));
};

const variantReviewSummary = (candidate: ErpMappingCandidate) => {
  const assessment = candidate.evidence.variantAssessment as { reviewRequired?: string[] } | undefined;
  return Array.isArray(assessment?.reviewRequired)
    ? `${assessment.reviewRequired.length} attributes unknown or requiring review · assembly fit not evaluated`
    : "Attribute assessment unavailable — re-discover to review";
};
const latestDiscovery = computed(() => latestRun.value?.requestConfig.discoveryEvaluation as Record<string, unknown> | undefined);

watch([validateErpMaterials, summitOnly], () => loadFirstPage());
watch(erpSourceFilter, () =>
  Promise.all([loadFirstPage(), loadCandidateFirstPage()])
);

onMounted(() =>
  Promise.all([
    loadCatalog(),
    loadRuns(),
    loadCandidates(),
    loadAiReviewStatus()
  ])
);
</script>

<style scoped lang="scss">
.mapping-page {
  min-width: 0;
}

.page-header {
  margin-bottom: 1rem;
  max-width: 58rem;

  h2 {
    font-size: 2rem;
    margin: 0 0 0.5rem;
  }

  p:last-child {
    color: var(--text-color-secondary);
    line-height: 1.5;
  }
}

.eyebrow {
  color: var(--red-500);
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0;
  margin: 0 0 0.25rem;
  text-transform: uppercase;
}

.filter-panel {
  margin-bottom: 1rem;
}

.automation-panel {
  margin-bottom: 1rem;
}

.automation-grid {
  align-items: end;
  display: grid;
  gap: 1rem;
  grid-template-columns:
    minmax(12rem, 1fr)
    minmax(12rem, 1fr)
    minmax(9rem, 0.5fr)
    auto;

  label {
    display: flex;
    flex-direction: column;
    font-weight: 700;
    gap: 0.4rem;
  }
}

.ai-review-status {
  align-items: center;
  display: flex;
  gap: 0.75rem;
  margin-top: 0.9rem;

  span {
    color: var(--text-color-secondary);
    font-size: 0.85rem;
    min-width: 0;
    overflow-wrap: anywhere;
  }
}

.run-strip {
  border-top: 1px solid var(--surface-300);
  display: grid;
  grid-template-columns: repeat(5, minmax(8rem, 1fr));
  margin-top: 1rem;

  > div {
    display: flex;
    flex-direction: column;
    gap: 0.2rem;
    padding: 0.75rem 1rem 0;
  }

  > div:first-child {
    padding-left: 0;
  }

  span {
    color: var(--text-color-secondary);
    font-size: 0.8rem;
    overflow-wrap: anywhere;
  }
}

.candidate-section {
  margin-bottom: 1rem;
}

.section-heading {
  align-items: end;
  display: flex;
  justify-content: space-between;
  margin-bottom: 0.75rem;

  h3 {
    font-size: 1.1rem;
    margin: 0 0 0.2rem;
  }

  span {
    color: var(--text-color-secondary);
    font-size: 0.82rem;
  }
}

.candidate-filters {
  display: flex;
  gap: 0.5rem;
}

.candidate-table {
  border: 1px solid var(--surface-300);
}

.score-cell,
.evidence-cell {
  display: flex;
  flex-direction: column;
  gap: 0.2rem;

  span {
    color: var(--text-color-secondary);
    font-size: 0.8rem;
  }
}

.row-actions {
  display: flex;
  gap: 0.4rem;
}

.candidate-details {
  background: var(--surface-50);
  display: grid;
  gap: 1.5rem;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  padding: 1rem 1.25rem;

  > div {
    min-width: 0;
  }

  dl {
    display: grid;
    gap: 0.35rem;
    margin: 0.75rem 0 0;
  }

  dl > div {
    display: flex;
    gap: 0.75rem;
    justify-content: space-between;
  }

  dt {
    color: var(--text-color-secondary);
  }

  dd {
    margin: 0;
    overflow-wrap: anywhere;
  }

  ul {
    margin: 0.75rem 0 0;
    padding-left: 1.25rem;
  }
}

.filter-grid {
  align-items: end;
  display: grid;
  gap: 1rem;
  grid-template-columns:
    minmax(12rem, 0.8fr)
    minmax(18rem, 1.5fr)
    minmax(10rem, 0.7fr)
    auto
    auto
    auto;

  label {
    display: flex;
    flex-direction: column;
    font-weight: 700;
    gap: 0.4rem;
  }
}

.search-field {
  :deep(.p-input-icon-left),
  :deep(.p-inputtext) {
    width: 100%;
  }
}

.validation-toggle {
  align-items: flex-start;
  min-width: 9.5rem;
}

.filter-actions {
  display: flex;
  gap: 0.5rem;
}

.summary-strip {
  background: var(--surface-0);
  border-bottom: 1px solid var(--surface-300);
  border-top: 1px solid var(--surface-300);
  display: grid;
  grid-template-columns: repeat(6, minmax(7rem, 1fr));
  margin-bottom: 1rem;

  > div {
    align-items: flex-start;
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
    min-width: 0;
    padding: 0.75rem 1rem;
  }

  > div + div {
    border-left: 1px solid var(--surface-300);
  }
}

.summary-value {
  font-size: 1.35rem;
  font-weight: 700;
}

.summary-label {
  color: var(--text-color-secondary);
  font-size: 0.8rem;
}

.mapping-table {
  border: 1px solid var(--surface-300);
}

.catalog-cell {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;

  span {
    color: var(--text-color-secondary);
    font-size: 0.82rem;
  }
}

code {
  font-family: var(--monospace-family);
  font-size: 0.8rem;
  overflow-wrap: anywhere;
}

.material-number-list {
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;

  > div {
    align-items: center;
    background: var(--surface-100);
    border: 1px solid var(--surface-300);
    border-radius: 0.25rem;
    display: flex;
    gap: 0.35rem;
    padding: 0.25rem 0.4rem;
  }
}

.muted {
  color: var(--text-color-secondary);
}

.mapping-details {
  background: var(--surface-50);
  border-top: 1px solid var(--surface-300);
  padding: 1rem 1.25rem 1.25rem;
}

.detail-heading {
  margin-bottom: 0.75rem;

  > div {
    display: flex;
    flex-direction: column;
    gap: 0.2rem;
  }

  span {
    color: var(--text-color-secondary);
    font-size: 0.82rem;
  }
}

.erp-material-grid {
  display: grid;
  gap: 0.75rem;
  grid-template-columns: repeat(auto-fit, minmax(20rem, 1fr));
}

.erp-material {
  background: var(--surface-0);
  border: 1px solid var(--surface-300);
  border-radius: 0.375rem;
  padding: 1rem;

  > strong {
    display: block;
    font-size: 0.95rem;
    margin: 0.75rem 0;
  }

  dl {
    display: grid;
    gap: 0.75rem;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    margin: 0;
  }

  dl > div {
    min-width: 0;
  }

  dt {
    color: var(--text-color-secondary);
    font-size: 0.72rem;
    font-weight: 700;
    margin-bottom: 0.2rem;
    text-transform: uppercase;
  }

  dd {
    margin: 0;
    overflow-wrap: anywhere;
  }
}

.material-heading {
  align-items: center;
  display: flex;
  justify-content: space-between;
  gap: 0.75rem;

  code {
    font-size: 0.95rem;
    font-weight: 700;
  }
}

.material-identity {
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
}

.qualifiers {
  border-top: 1px solid var(--surface-200);
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem 1rem;
  margin-top: 0.75rem;
  padding-top: 0.75rem;
}

.unresolved-detail,
.empty-state {
  color: var(--text-color-secondary);
  padding: 1.5rem;
  text-align: center;
}

.error-banner {
  background: var(--red-50);
  border: 1px solid var(--red-200);
  border-radius: 0.375rem;
  color: var(--red-700);
  margin: 1rem 0;
  padding: 0.875rem 1rem;
}

:deep(.expander-column) {
  width: 3rem;
}

@media (max-width: 1100px) {
  .automation-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .run-strip {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  .filter-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .summary-strip {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .summary-strip > div:nth-child(3) {
    border-left: 0;
    border-top: 1px solid var(--surface-300);
  }

  .summary-strip > div:nth-child(4) {
    border-top: 1px solid var(--surface-300);
  }
}

@media (max-width: 640px) {
  .automation-grid,
  .filter-grid,
  .summary-strip,
  .run-strip {
    grid-template-columns: 1fr;
  }

  .section-heading {
    align-items: stretch;
    flex-direction: column;
    gap: 0.75rem;
  }

  .run-strip > div,
  .run-strip > div:first-child {
    padding-left: 0;
  }

  .filter-actions {
    justify-content: stretch;

    :deep(.p-button:first-child) {
      flex: 1;
    }
  }

  .summary-strip > div + div {
    border-left: 0;
    border-top: 1px solid var(--surface-300);
  }

  .erp-material-grid,
  .erp-material dl,
  .candidate-details {
    grid-template-columns: 1fr;
  }
}
</style>
